import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { INQUIRY_FIELD_TYPES, INQUIRY_FIELD_TYPE_LABELS, type InquiryFieldType, type InquiryType, type InquiryTypeInput } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useInquiryTypes } from '../inquiryTypes';
import { useSession } from '../session';

interface OptionDraft {
  /** Bestehender Wert. Fehlt er, wird er beim Speichern aus der Beschriftung gebildet und bleibt dann unverändert. */
  value: string | null;
  label: string;
}

interface FieldDraft {
  /** Schlüssel, unter dem die Webseite den Wert sendet. Bei bestehenden Feldern nicht änderbar. */
  key: string;
  isNew: boolean;
  keyTouched: boolean;
  label: string;
  type: InquiryFieldType;
  required: boolean;
  unit: string;
  options: OptionDraft[];
}

/** "Größe in m²" → "groesse_in_m" */
function slug(text: string, separator = '_'): string {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, separator)
    .replace(new RegExp(`^${separator}+|${separator}+$`, 'g'), '');
}

const fieldKeyFrom = (label: string) => {
  const key = slug(label).slice(0, 60);
  return /^[a-z]/.test(key) ? key : key ? `f_${key}`.slice(0, 60) : '';
};

function toDraft(type: InquiryType): FieldDraft[] {
  return type.fields.map((f) => ({
    key: f.key,
    isNew: false,
    keyTouched: true,
    label: f.label,
    type: f.type,
    required: f.required,
    unit: f.unit ?? '',
    options: (f.options ?? []).map((o) => ({ value: o.value, label: o.label })),
  }));
}

/** Meldung der API zu einem Feld der Eingabe in verständlicher Form */
function describeErrors(fields: Record<string, string>): string[] {
  return Object.entries(fields).map(([path, message]) => {
    const field = /^fields\.(\d+)\.?(.*)$/.exec(path);
    if (field) return `Feld ${Number(field[1]) + 1}: ${message}`;
    if (path === 'key') return `Kennung: ${message}`;
    if (path === 'label') return `Bezeichnung: ${message}`;
    return message;
  });
}

/**
 * Anfragetyp anlegen oder bearbeiten, mit seinen Feldern. Ein Feld, das in Anfragen vorkommt, lässt sich
 * nicht löschen und nicht auf einen anderen Feldtyp umstellen. Das prüft der Server und meldet es hier.
 */
export function InquiryTypePage() {
  const { key: routeKey } = useParams();
  const isCreate = !routeKey;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can } = useSession();
  const types = useInquiryTypes();
  const existing = routeKey ? types.get(routeKey) : undefined;
  const canDelete = can('setup.delete');

  const [typeKey, setTypeKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [label, setLabel] = useState('');
  const [fields, setFields] = useState<FieldDraft[]>([]);
  const [visibleRoleKeys, setVisibleRoleKeys] = useState<string[]>([]);
  const roles = useQuery({ queryKey: ['inquiry-type-roles'], queryFn: async () => (await api<{ data: { key: string; name: string }[] }>('GET', '/inquiry-types/roles')).data });
  const [problems, setProblems] = useState<string[]>([]);
  const loaded = useRef(false);

  // Bestehenden Typ einmal in die Eingabe übernehmen, sobald die Liste geladen ist
  useEffect(() => {
    if (loaded.current || !existing) return;
    loaded.current = true;
    setTypeKey(existing.key);
    setLabel(existing.label);
    setFields(toDraft(existing));
    setVisibleRoleKeys(existing.visibleRoleKeys);
  }, [existing]);

  const save = useMutation({
    mutationFn: (input: InquiryTypeInput) =>
      isCreate ? api<{ data: InquiryType }>('POST', '/inquiry-types', input) : api<{ data: InquiryType }>('PUT', `/inquiry-types/${routeKey}`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['inquiry-types'] });
      navigate('/einrichtung/anfragetypen', { replace: true });
    },
    onError: (e) => {
      // Die Meldung steht oben, gespeichert wird meist unten auf der langen Seite
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError) setProblems(e.status === 422 && Object.keys(e.fields).length ? [e.message, ...describeErrors(e.fields)] : [e.message]);
      else setProblems(['Die Änderungen konnten nicht gespeichert werden.']);
    },
  });

  const patch = (index: number, change: Partial<FieldDraft>) => setFields((list) => list.map((f, i) => (i === index ? { ...f, ...change } : f)));
  const move = (index: number, delta: -1 | 1) =>
    setFields((list) => {
      const target = index + delta;
      if (target < 0 || target >= list.length) return list;
      const next = [...list];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });

  function submit(event: FormEvent) {
    event.preventDefault();
    setProblems([]);
    const sortOrder = existing ? existing.sortOrder : Math.max(0, ...types.types.map((t) => t.sortOrder)) + 10;
    const input: InquiryTypeInput = {
      key: typeKey.trim(),
      label: label.trim(),
      sortOrder,
      visibleRoleKeys,
      fields: fields.map((f) => ({
        key: f.key.trim(),
        label: f.label.trim(),
        type: f.type,
        required: f.required,
        ...(f.type === 'number' && f.unit.trim() ? { unit: f.unit.trim() } : {}),
        ...(f.type === 'select' || f.type === 'multiselect'
          ? { options: f.options.filter((o) => o.label.trim()).map((o) => ({ value: o.value ?? slug(o.label).slice(0, 60), label: o.label.trim() })) }
          : {}),
      })),
    };
    save.mutate(input);
  }

  if (!isCreate && types.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && !existing) {
    return (
      <div className="card narrow">
        <h1>Anfragetyp nicht gefunden</h1>
        <Link to="/einrichtung/anfragetypen">Zu den Anfragetypen</Link>
      </div>
    );
  }

  return (
    <>
      <p><Link to="/einrichtung/anfragetypen" className="back-link">← Anfragetypen</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Neuer Anfragetyp' : existing!.label}</h1>
      </div>

      <form className="card" onSubmit={submit} noValidate>
        {problems.length > 0 && (
          <div className="alert alert--error" role="alert">
            {problems.map((p, i) => <div key={i}>{p}</div>)}
          </div>
        )}

        <div className="form-grid">
          <label className="field">
            <span>Bezeichnung <abbr title="Pflichtfeld">*</abbr></span>
            <input
              className="input"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                if (isCreate && !keyTouched) setTypeKey(slug(e.target.value, '-').slice(0, 40));
              }}
              placeholder="z. B. Umzug"
            />
          </label>
          <label className="field">
            <span>Kennung</span>
            <input className="input" value={typeKey} disabled={!isCreate} onChange={(e) => { setTypeKey(e.target.value.toLowerCase()); setKeyTouched(true); }} placeholder="z. B. umzug" />
            <small className="muted">{isCreate ? 'Unter dieser Kennung sendet die Webseite den Typ. Nur a–z, 0–9, - und _. Später nicht mehr änderbar.' : 'Die Kennung lässt sich nicht ändern, weil die Webseite sie sendet.'}</small>
          </label>
        </div>

        <fieldset className="field-card">
          <legend>Wer sieht Anfragen dieses Typs?</legend>
          <small className="muted">
            Ohne Auswahl sehen alle Rollen mit dem Recht „Anfragen ansehen“ diesen Typ. Mit Auswahl nur die gewählten Rollen. Der Inhaber sieht immer alle. Zum Beispiel sieht dann nur der HR Manager die Bewerbungen.
          </small>
          <div className="shift-days">
            {(roles.data ?? []).map((r) => (
              <label key={r.key} className="chip">
                <input type="checkbox" checked={visibleRoleKeys.includes(r.key)} onChange={(e) => setVisibleRoleKeys((list) => (e.target.checked ? [...list, r.key] : list.filter((k) => k !== r.key)))} /> {r.name}
              </label>
            ))}
            {roles.isLoading && <span className="muted small">Rollen werden geladen …</span>}
          </div>
          {visibleRoleKeys.length === 0 ? <small className="muted">Aktuell: alle Rollen mit Leserecht.</small> : <small className="muted">Aktuell nur: {(roles.data ?? []).filter((r) => visibleRoleKeys.includes(r.key)).map((r) => r.name).join(', ')}.</small>}
        </fieldset>

        <h2>Felder</h2>
        <p className="muted small">
          Die Kennung eines Felds ist der Name, unter dem die Webseite den Wert sendet. Ein Feld, das in Anfragen vorkommt, lässt sich nicht löschen
          und nicht auf einen anderen Feldtyp umstellen. Beschriftung, Einheit und Reihenfolge ändern Sie jederzeit.
        </p>

        {fields.length === 0 && <p className="empty">Noch keine Felder.</p>}
        {fields.map((f, index) => (
          <fieldset key={index} className="field-card">
            <legend>Feld {index + 1}</legend>
            <div className="form-grid">
              <label className="field">
                <span>Beschriftung <abbr title="Pflichtfeld">*</abbr></span>
                <input
                  className="input"
                  value={f.label}
                  onChange={(e) => patch(index, { label: e.target.value, ...(f.isNew && !f.keyTouched ? { key: fieldKeyFrom(e.target.value) } : {}) })}
                  placeholder="z. B. Auszugsadresse"
                />
              </label>
              <label className="field">
                <span>Kennung</span>
                <input className="input" value={f.key} disabled={!f.isNew} onChange={(e) => patch(index, { key: e.target.value, keyTouched: true })} placeholder="z. B. from_address" />
              </label>
              <label className="field">
                <span>Feldtyp</span>
                <select className="input" value={f.type} onChange={(e) => patch(index, { type: e.target.value as InquiryFieldType })}>
                  {INQUIRY_FIELD_TYPES.map((t) => <option key={t} value={t}>{INQUIRY_FIELD_TYPE_LABELS[t]}</option>)}
                </select>
              </label>
              {f.type === 'number' && (
                <label className="field">
                  <span>Einheit</span>
                  <input className="input" value={f.unit} onChange={(e) => patch(index, { unit: e.target.value })} placeholder="z. B. m²" maxLength={20} />
                </label>
              )}
            </div>

            {(f.type === 'select' || f.type === 'multiselect') && (
              <div className="field">
                <span>Auswahlmöglichkeiten</span>
                {f.options.map((o, oi) => (
                  <div key={oi} className="option-row">
                    <input
                      className="input"
                      value={o.label}
                      aria-label={`Auswahl ${oi + 1}`}
                      onChange={(e) => patch(index, { options: f.options.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)) })}
                    />
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => patch(index, { options: f.options.filter((_, xi) => xi !== oi) })}>Entfernen</button>
                  </div>
                ))}
                <div>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => patch(index, { options: [...f.options, { value: null, label: '' }] })}>Auswahl hinzufügen</button>
                </div>
              </div>
            )}

            <div className="field-card__foot">
              <label className="check">
                <input type="checkbox" checked={f.required} onChange={(e) => patch(index, { required: e.target.checked })} />
                <span>Pflichtfeld</span>
              </label>
              <div className="row-actions">
                <button type="button" className="btn btn--secondary btn--sm" aria-label={`Feld ${index + 1} nach oben`} disabled={index === 0} onClick={() => move(index, -1)}>↑</button>
                <button type="button" className="btn btn--secondary btn--sm" aria-label={`Feld ${index + 1} nach unten`} disabled={index === fields.length - 1} onClick={() => move(index, 1)}>↓</button>
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  disabled={!f.isNew && !canDelete}
                  title={!f.isNew && !canDelete ? 'Zum Löschen bestehender Felder fehlt Ihnen das Recht.' : undefined}
                  onClick={() => setFields((list) => list.filter((_, i) => i !== index))}
                >
                  Feld löschen
                </button>
              </div>
            </div>
          </fieldset>
        ))}

        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => setFields((list) => [...list, { key: '', isNew: true, keyTouched: false, label: '', type: 'text', required: false, unit: '', options: [] }])}
        >
          Feld hinzufügen
        </button>

        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
          <Link className="btn btn--secondary" to="/einrichtung/anfragetypen">Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
